import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useGetBrandSubscriptionFeaturesQuery } from '../services/nestjs-base-api';
import { SubscriptionPlanEnum } from '../services/nestjs-base-api/types';

export enum SubscriptionFeature {
    Benchmarking = 'benchmarking',
    AdvancedAI = 'advanced_ai',
    BasicAI = 'basic_ai',
    BrandEquity = 'brand_equity',
}

interface UseSubscriptionFeaturesResult {
    hasFeature: (feature: SubscriptionFeature) => boolean;
    isLoading: boolean;
    error: any;
    plan: SubscriptionPlanEnum | null;
    hasActiveSubscription: boolean;
    subscriptionStatus: string | null;
    bypassEnabled: boolean;
    requiresUpgrade: (feature: SubscriptionFeature) => {
        required: boolean;
        currentPlan: SubscriptionPlanEnum | null;
        requiredPlan: SubscriptionPlanEnum;
        message: string;
    };
}

export const useSubscriptionFeatures = (brandId?: number): UseSubscriptionFeaturesResult => {
    const { user } = useAuth();

    const { 
        data: subscriptionFeatures, 
        isLoading, 
        error 
    } = useGetBrandSubscriptionFeaturesQuery(
        { jwtToken: user!.jwtToken, brandId: brandId! },
        { skip: !user?.jwtToken || !brandId }
    );

    const result = useMemo(() => {
        const hasActiveSubscription = subscriptionFeatures?.hasActiveSubscription || false;
        const plan = subscriptionFeatures?.plan || null;
        const status = subscriptionFeatures?.status || null;
        const features = subscriptionFeatures?.features;
        const bypassEnabled = subscriptionFeatures?.bypassEnabled || false;

        const hasFeature = (feature: SubscriptionFeature): boolean => {
            if (!hasActiveSubscription || !features) {
                return false;
            }

            switch (feature) {
                case SubscriptionFeature.Benchmarking:
                    return features.benchmarking;
                case SubscriptionFeature.AdvancedAI:
                    return features.aiExpertLevel === 'advanced';
                case SubscriptionFeature.BasicAI:
                    return features.aiExpertLevel === 'basic' || features.aiExpertLevel === 'advanced';
                case SubscriptionFeature.BrandEquity:
                    return hasActiveSubscription; // Brand equity requires any active subscription (Professional or Enterprise)
                default:
                    return false;
            }
        };

        const requiresUpgrade = (feature: SubscriptionFeature) => {
            const hasCurrentFeature = hasFeature(feature);
            
            if (hasCurrentFeature) {
                return {
                    required: false,
                    currentPlan: plan,
                    requiredPlan: SubscriptionPlanEnum.Professional,
                    message: 'Feature is available with your current subscription'
                };
            }

            // Determine required plan for feature
            let requiredPlan: SubscriptionPlanEnum;
            let message: string;

            switch (feature) {
                case SubscriptionFeature.Benchmarking:
                case SubscriptionFeature.AdvancedAI:
                    requiredPlan = SubscriptionPlanEnum.Enterprise;
                    message = `${feature === SubscriptionFeature.Benchmarking ? 'Benchmarking' : 'Advanced AI'} requires an upgrade`;
                    break;
                case SubscriptionFeature.BasicAI:
                case SubscriptionFeature.BrandEquity:
                    requiredPlan = SubscriptionPlanEnum.Professional;
                    message = feature === SubscriptionFeature.BrandEquity
                        ? 'Brand equity calculations require a paid subscription'
                        : 'AI features require a paid subscription';
                    break;
                default:
                    requiredPlan = SubscriptionPlanEnum.Professional;
                    message = 'This feature requires a subscription';
            }

            if (!hasActiveSubscription) {
                message = `No active subscription found. ${message}`;
            } else if (plan === SubscriptionPlanEnum.Professional && requiredPlan === SubscriptionPlanEnum.Enterprise) {
                message = `Upgrade to access ${feature === SubscriptionFeature.Benchmarking ? 'Benchmarking' : 'Advanced AI'}`;
            }

            return {
                required: true,
                currentPlan: plan,
                requiredPlan,
                message
            };
        };

        return {
            hasFeature,
            isLoading,
            error,
            plan,
            hasActiveSubscription,
            subscriptionStatus: status,
            bypassEnabled,
            requiresUpgrade,
        };
    }, [subscriptionFeatures, isLoading, error]);

    return result;
};

// Convenience hooks for specific features
export const useBenchmarkingAccess = (brandId?: number) => {
    const { hasFeature, requiresUpgrade, isLoading } = useSubscriptionFeatures(brandId);
    return {
        hasAccess: hasFeature(SubscriptionFeature.Benchmarking),
        upgrade: requiresUpgrade(SubscriptionFeature.Benchmarking),
        isLoading,
    };
};

export const useAIAccess = (brandId?: number) => {
    const { hasFeature, requiresUpgrade, isLoading } = useSubscriptionFeatures(brandId);
    return {
        hasBasicAI: hasFeature(SubscriptionFeature.BasicAI),
        hasAdvancedAI: hasFeature(SubscriptionFeature.AdvancedAI),
        upgradeForBasic: requiresUpgrade(SubscriptionFeature.BasicAI),
        upgradeForAdvanced: requiresUpgrade(SubscriptionFeature.AdvancedAI),
        isLoading,
    };
};